import type {
  Resource,
  ResourceEntity,
  ResourceTypeEntityResponseCollection,
} from '@edwin-group/cj-graphql/generated/graphql';
import { QUERY_FETCH_RESOURCE_HUB_PAGE } from '@edwin-group/cj-graphql';

import type { ResourceHubPageQuery, ResourceHubPageQueryVariables } from '@edwin-group/cj-graphql';

import { useQueryCacheAndNetwork } from '@/hooks/useQueryCacheAndNetwork';

import { Loading } from './loading';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchAwarenessDaysResources } from '@/services/resource-hub/resource-hub.service';
import Divider from '@/components/Divider';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import ResourceCard from '@/components/Card/Resource';

function renderCards({
  title,
  data,
  viewAll = false,
  showDivider = true,
  viewAllText = 'View All',
  isWellbeingCalendar = false,
}: {
  title: string;
  data: ResourceEntity[];
  showDivider?: boolean;
  viewAll?: boolean;
  viewAllText?: string;
  isWellbeingCalendar?: boolean;
}) {
  if (!data?.length) {
    return null;
  }

  const firstItem = data[0];
  const type = (firstItem?.attributes?.resourceType as unknown as ResourceTypeEntityResponseCollection)?.data?.[0]
    ?.attributes?.type;

  let viewAllUrl = '/resource-hub';

  if (type) {
    viewAllUrl = `/resource-hub/type/${type}`;
  }

  if (isWellbeingCalendar) {
    viewAllUrl = '/calendar';
  }

  return (
    <AnimatedFadeInComponent key={title}>
      <div key={title}>
        <div className="flex items-center justify-between">
          <h3 className="mb-sh-15 font-cadiz text-sh-28-32 font-bold">{title}</h3>
          {viewAll ? (
            <Link className="text-sh-xxxs font-bold text-primary-600" to={viewAllUrl}>
              {viewAllText}
            </Link>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-sh-30 md:grid-cols-3 md:gap-4">
          {data?.map((resource: ResourceEntity) => {
            return <ResourceCard resource={resource as Resource} key={resource?.attributes?.slug} />;
          })}
        </div>
      </div>
      {showDivider ? <Divider /> : null}
    </AnimatedFadeInComponent>
  );
}

export default function AwarenessCalendarResources() {
  const [awarenessCalendarResources, setAwarenessDaysResources] = useState<ResourceEntity[]>([]);

  const { data: resourceHubPage, loading } = useQueryCacheAndNetwork<
    ResourceHubPageQuery,
    ResourceHubPageQueryVariables
  >(QUERY_FETCH_RESOURCE_HUB_PAGE);

  useEffect(() => {
    const fetchAllResources = async () => {
      const data = await fetchAwarenessDaysResources();
      setAwarenessDaysResources(data?.resources || []);
    };

    fetchAllResources();
  }, [resourceHubPage?.resourceTypes]);

  if (loading) return <Loading />;

  return renderCards({
    title: `${resourceHubPage?.resourceHubPage?.data?.attributes?.awarenessResourcesTitle}` || '',
    data: awarenessCalendarResources?.slice(0, 2) || [],
    viewAll: awarenessCalendarResources?.length > 2,
    showDivider: true,
  });
}
